import PeopleWidgetWithOutHolo from 'Components/common/components/PeopleWidgetWithOutHolo';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ModalSection, NotificationAlerts, NotificationOpenedRequest } from 'Types/CompaniesTypes';
import { backgroundColorParsing, feedHighlightIcons, getQueryPersonId, highlightsStatusOptions, valueToObject } from 'Utils/UtilityFunctions';
import { actions as notesSlice } from 'Slice/NotesSlice';
import { actions as uiSlice } from 'Slice/UISlice';
import {
  maxWidth, primaryFont, retina, wMBold, wMedium, minRegularScreen, SecondaryColor
} from 'Utils/utilsStyle';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { COMPANY_OVERVIEW_ROUTE, GROUP_DETAIL_ROUTE, PERSONALITY_INSIGHTS_ROUTE } from 'Utils/Constants';
import { actions as alertActions } from '../../../Slice/AlertsSlice';
import FeedHighlightsNotificationItems from './FeedHighlightsNotificationItems';
import { useMemo, useState } from 'react';
import HighlightModal from '../../common/modal/HighlightModal';
import { IsMobile } from 'Utils/UtilityFunctions';

type Props = {
  notification: NotificationAlerts,
  isRead?: boolean,
  personType?: string,
  closeDrawer?: () => void,
} & typeof defaultProps;

const defaultProps = {
  isRead: false,
  personType: 'EXECUTIVE',
  closeDrawer: () => { },
};

const ArticleWrapper = styled.div<{ $isRead?: boolean; $isMobile?: boolean }>`
&.article-item {
    padding: ${({ $isMobile }) => (!$isMobile ? '18px 8px' : '8px')};
    border-radius: 10px;
    margin-bottom: 8px;
    align-items: ${({ $isMobile }) => ($isMobile && 'start')};
    gap: 10px;
}
  .ant-popover-inner {
    border-radius: 16px;
    .ant-popover-inner-content{
      padding: 0;
    }
  }
  .text-area {
    position: relative;
    padding-top: 4px;
    width: 100%;
    .message {
      &.notBold {
        font-weight: ${wMedium};
        margin-left: 3px;
      }
      font-family: ${primaryFont};
      font-weight: ${wMBold};
      font-size: 14px;
      letter-spacing:-0.318px;
      line-height: 16px;
      color:${SecondaryColor};
      @media all and (max-width: ${minRegularScreen}) {
        font-size: 12px;
      }
    }
  }
`;

const MODAL_SECTIONS: ModalSection[] = [
  'sales_insights',
  'personalized_outreach',
  'role_specific_challenges',
  'engagement_opportunities',
  'gilroy_insight',
];
const FLURRY_EVENT_NAMES: string[] = [
  'Highlight_Sales_Angle_Clicked',
  'Highlight_Outreach_Message_Clicked',
  'Highlight_Role_Challenges_Clicked',
  'Highlight_Engagement_Opportunities_Clicked',
  'Highlight_Ask_Gilroy_Clicked',
];

const HIGHLIGHT_ICONS = feedHighlightIcons.map((option) => option.icon);

const FeedHighlightsNotification = function FeedHighlightsNotification(props: Props) {
  const {
    notification,
    isRead,
    personType,
    closeDrawer,
  } = props;

  const {
    personalityTypes, personId, personImage, personName,
    personDesignation, newCompany, oldTitle, oldCompany, oldIndustry, date: joinDate, dateTimeStamp,
    messageType, notificationType, notificationID, groupName, id,
    groupId, noteAddOnPersonId, noteAddedOnPersonName, noteAddOnCompanyId, noteAddedOnCompanyName,
    priority, hasInsights
  } = notification;
  const shareBuyerGroup = 'ShareBuyerGroup';
  const personQueryId = getQueryPersonId();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const host = window.location.href;
  const isMobile = IsMobile();

  const selectedStatus = useMemo(() => highlightsStatusOptions.find(
    (option) => option.value === priority
  ), [priority]);

  const status = useMemo(() => (selectedStatus
    ? [{
      label: selectedStatus.label,
      icon: selectedStatus.icon,
      bgColor: selectedStatus.bgColor,
      color: selectedStatus.color,
      borderColor: selectedStatus.borderColor,
    }]
    : []), [selectedStatus]);

  const prevRole = oldTitle || '';
  const industry = oldIndustry || '';

  const navigateTo = () => {
    dispatch(notesSlice.setAlertsNotificationId(notificationID));
    if (notificationType === 'person') {
      const mType = messageType.toLowerCase();
      let type = '';
      switch (mType) {
        case 'company':
          type = 'job_change';
          break;
        case 'sharebuyergroup':
          type = 'buyer_group_shared';
          break;
        case 'persontag':
          type = 'notes_tagged';
          break;
        case 'person':
          type = 'notes_add';
          break;
        case 'designation':
          type = 'designation_change';
          break;
        default:
          type = '';
      }
      if (mType.includes('company') && noteAddOnCompanyId) {
        dispatch(flurryActions.callFlurryEvent(
          valueToObject('alertPanel_companyNotes_clicked', 0, {
            host,
          }),
        ));
      } else {
        dispatch(flurryActions.callFlurryEvent(
          valueToObject('Highlight_people_clicked', 0, {
            ...type && { alert_selected: type },
            host, personId, tags_input: 'Person'
          }),
        ));
        if (messageType === 'Person' || messageType === 'PersonTag') {
          dispatch(uiSlice.isToastNeedToOpen(true));
        }
      }

      if (messageType === shareBuyerGroup && groupId) {
        navigate(`${GROUP_DETAIL_ROUTE}${groupId}`);
      } else if ((messageType === 'Person' || messageType === 'PersonTag') && noteAddOnPersonId) {
        navigate(`${PERSONALITY_INSIGHTS_ROUTE}${noteAddOnPersonId}&personType=${personType}&isNotes=true`);
      } else if ((messageType === 'Company' || messageType === 'CompanyTag') && !newCompany && noteAddOnCompanyId) {
        navigate(`${COMPANY_OVERVIEW_ROUTE}${noteAddOnCompanyId}&isNotes=true`);
      } else if (messageType === 'Company' && newCompany && personId) {
        navigate(`${PERSONALITY_INSIGHTS_ROUTE}${personId}&personType=${personType}`);
      } else if (personId && personQueryId !== personId) {
        navigate(`${PERSONALITY_INSIGHTS_ROUTE}${personId}&personType=${personType}`);
      }
      const notificationData: NotificationOpenedRequest = {
        notificationID: id,
        messageType,
      };
      if (!isRead) dispatch(alertActions.getNotificationOpened(notificationData));
      closeDrawer();
    }
  };

  const [modalVisible, setModalVisible] = useState(false);
  const [activeModalSection, setActiveModalSection] = useState<ModalSection>('sales_insights');

  const openModal = (e: React.MouseEvent, section: ModalSection) => {
    e.stopPropagation();
    setActiveModalSection(section);
    setModalVisible(true);
  }

  const onHighlightIconClick = (e: React.MouseEvent, index: number) => {
    const section = MODAL_SECTIONS[index];
    if (section) {
      openModal(e, section);
      dispatch(flurryActions.callFlurryEvent(
        valueToObject(FLURRY_EVENT_NAMES[index], 0, { personId, host, tags_input: 'Person' })
      ));
    }
  };

  const renderHighlightItems = () => (
    <span className="feed-notification-highlight-items">
      <FeedHighlightsNotificationItems
        timeStamp={dateTimeStamp}
        status={status}
        prevRole={prevRole}
        company={oldCompany}
        industry={industry}
        icons={HIGHLIGHT_ICONS}
        onIconClick={onHighlightIconClick}
        hasInsights={hasInsights}
      />
    </span>
  );

  return (
    <>
      {modalVisible && (
        <HighlightModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          initialSection={activeModalSection}
          notificationId={notificationID}
          personId={personId}
        />
      )
      }
      <ArticleWrapper onClick={navigateTo} className={`article-item alert-article ${isRead ? '' : 'active'}`} $isRead={isRead} $isMobile={isMobile} >
        <div role="button" tabIndex={0}>
          <PeopleWidgetWithOutHolo
            key={personId}
            diameter="54px"
            diameterSm="54px"
            diameterXsm="54px"
            imageClassName="person-image"
            marginSm="0px 0px 0px 0px"
            margin="0px 0px 0px 0px"
            wrapperColor={backgroundColorParsing(personalityTypes)}
            logo={personImage}
            personId={personId}
            ringPadding="3px"
            ringPaddingSm="3px"
            disableNavigation
          />
        </div>
        {messageType === 'Person' && noteAddedOnPersonName ? (
          <div className="text-area">
            <span
              className="message text-truncate-three-line"
            >
              {personName}
              <span className="message notBold">
                has posted a note on
                {' '}
                <span className="message">
                  {noteAddedOnPersonName}
                  &apos;s
                  {' '}
                </span>
                profile
              </span>
            </span>
            {renderHighlightItems()}
          </div>
        ) : null}
        {messageType === 'PersonTag' ? (
          <div className="text-area">
            <span
              className="message text-truncate-three-line"
            >
              {personName}
              <span className="message notBold">
                mentioned you in a note on
                {' '}
                <span className="message">
                  {noteAddedOnPersonName}
                  &apos;s
                  {' '}
                </span>
                profile
              </span>
            </span>
            {renderHighlightItems()}
          </div>
        ) : null}
        {messageType === 'Company' && noteAddedOnCompanyName ? (
          <div className="text-area">
            <span
              className="message text-truncate-three-line"
            >
              {personName}
              <span className="message notBold">
                has posted a note on
                {' '}
                <span className="message">
                  {noteAddedOnCompanyName}
                  &apos;s
                  {' '}
                </span>
                profile
              </span>
            </span>
            {renderHighlightItems()}
          </div>
        ) : null}
        {messageType === 'CompanyTag' ? (
          <div className="text-area">
            <span
              className="message text-truncate-three-line"
            >
              {personName}
              <span className="message notBold">
                mentioned you in a note on
                {' '}
                <span className="message">
                  {noteAddedOnCompanyName}
                  &apos;
                  {' '}
                </span>
                profile
              </span>
            </span>
            {renderHighlightItems()}
          </div>
        ) : null}
        {messageType === 'Designation'
          ? (
            <div className="text-area">
              <span
                className="message text-truncate-three-line"
              >
                {personName}
                <span className="message notBold">has moved to a new role as a </span>
                {personDesignation}
                <span className="message notBold">at </span>
                {oldCompany}
              </span>
              {renderHighlightItems()}
            </div>
          ) : null}
        {messageType === shareBuyerGroup ? (
          <div className="text-area">
            <span
              className="message text-truncate-three-line"
            >
              {personName}
              <span className="message notBold">shared the group </span>
              {groupName}
              <span className="message notBold">
                with you
              </span>
            </span>
            {renderHighlightItems()}
          </div>
        ) : null}
        {messageType === 'Company' && newCompany ? (
          <div className="text-area">
            <span
              className="message text-truncate-three-line"
            >
              {personName}
              <span className="message notBold">has moved to </span>
              {newCompany}
              <span className="message notBold">from </span>
              {oldCompany}
              <span className="message notBold">as a </span>
              {personDesignation}
            </span>
            {renderHighlightItems()}
          </div>
        ) : null}
        {messageType === 'UnemployedPerson' ? (
          <div className="text-area">
            <span
              className="message text-truncate-three-line"
            >
              {personName}
              <span className="message notBold">has left their position at </span>
              {oldCompany}
            </span>
            {renderHighlightItems()}
          </div>
        ) : null}
      </ArticleWrapper>
    </>
  );
};
FeedHighlightsNotification.defaultProps = defaultProps;
export default FeedHighlightsNotification;
